import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import clsx from 'clsx';

export type ColorMode = 'solid' | 'gradient';

interface ColorSwitchProps {
  className?: string;
  value?: ColorMode;
  canChange?: boolean;
  onChange?: (mode: ColorMode) => void;
}

const toggles: ColorMode[] = ['solid', 'gradient'];

export const ColorSwitch: React.FC<ColorSwitchProps> = ({
  className,
  value = 'solid',
  canChange = true,
  onChange,
}) => {
  const [selected, setSelected] = useState<ColorMode>(value);
  const [highlightStyle, setHighlightStyle] = useState({ width: 0, left: 0 });
  const [isAnimated, setIsAnimated] = useState(false);

  const optionRefs = useRef<Record<ColorMode, HTMLButtonElement | null>>({
    solid: null,
    gradient: null,
  });

  useEffect(() => {
    if (value !== selected) setSelected(value);
  }, [value]);

  useLayoutEffect(() => {
    const activeEl = optionRefs.current[selected];
    if (activeEl) {
      setHighlightStyle({ width: activeEl.offsetWidth, left: activeEl.offsetLeft });
    }
  }, [selected]);

  const handleClick = (mode: ColorMode) => {
    if (!canChange) return;
    setIsAnimated(true);
    setSelected(mode);
    onChange?.(mode);
  };

  return (
    <div
      className={clsx(
        'relative flex h-8 w-28 items-center overflow-hidden rounded-md bg-neutral-50 shadow-[inset_0px_0px_2px_0px_rgba(0,0,0,0.12)]',
        className,
      )}
    >
      <div
        className={clsx(
          'absolute top-0 z-20 flex h-full items-center justify-center rounded-md bg-[#ffffff] text-indigo-500 outline outline-offset-[-1px] outline-[#e9ecf0]',
          isAnimated && 'transition-all duration-300',
        )}
        style={{
          width: `${highlightStyle.width ? highlightStyle.width + 1 : 0}px`,
          transform: `translateX(${highlightStyle.width ? highlightStyle.left - 1 / 2 : highlightStyle.left}px)`,
        }}
      >
        <p className="text-button-sm font-semibold capitalize">{selected}</p>
      </div>

      {toggles.map((opt) => (
        <button
          disabled={!canChange}
          key={opt}
          ref={(el) => void (optionRefs.current[opt] = el)}
          onClick={() => handleClick(opt)}
          className="text-button-sm relative z-10 flex-1 cursor-pointer px-2 py-1 text-center text-neutral-400 capitalize disabled:cursor-not-allowed disabled:opacity-50"
        >
          {opt}
        </button>
      ))}
    </div>
  );
};

export default ColorSwitch;
